import AssignmentOutlined from "@mui/icons-material/AssignmentOutlined";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import Close from "@mui/icons-material/Close";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Download from "@mui/icons-material/Download";
import Edit from "@mui/icons-material/Edit";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import Fullscreen from "@mui/icons-material/Fullscreen";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import ScienceOutlined from "@mui/icons-material/ScienceOutlined";
import SendOutlined from "@mui/icons-material/SendOutlined";
import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { FilePreview } from "components/Document/shared/FilePreview";
import { isPreviewable } from "components/Document/shared/fileTypeUtils";
import { FullScreenDocumentPreview } from "components/Document/shared/FullScreenDocumentPreview";
import { type DocumentType, type TierIiReportKind } from "generated-graphql/graphql";
import { useDocumentDownload } from "hooks/useDocumentDownload";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useCallback, useEffect, useMemo, useState } from "react";
import { documentTypeToLabel, reportKindToLabel } from "util/constants";
import { prettyPrintDateMed } from "util/dates";

export interface DocumentDetailDrawerDocument {
  id: string;
  title: string;
  documentType: string;
  fileExtension?: string | null;
  storageLink?: string | null;
  createdAt?: string | null;
  authoredAt?: string | null;
  description?: string | null;
  sizeMegabytes?: number | null;
  currentSubmissionDocument?: boolean | null;
  facilities?: Array<{ id: string; name: string }> | null;
  chemicalDocument?: Array<{ chemical: { id: string; name: string } }> | null;
  documentTags?: Array<{ id: string; name: string }> | null;
  associatedReports?: Array<{
    reportId?: string | null;
    submissionId?: string | null;
    reportingYear: number;
    programArea: string;
    reportKind?: string | null;
  }>;
}

interface DocumentDetailDrawerProps<T extends DocumentDetailDrawerDocument> {
  document: T | undefined;
  open: boolean;
  onClose: () => void;
  onEdit?: (doc: T) => void;
  tenantId: string | undefined;
  tenantName?: string;
}

export function DocumentDetailDrawer<T extends DocumentDetailDrawerDocument>({
  document,
  open,
  onClose,
  onEdit,
  tenantId,
  tenantName,
}: DocumentDetailDrawerProps<T>) {
  const { fetchAndHandleDownload } = useDocumentDownload();
  const [getDownloadLink] = useDocumentDownloadLink();
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fullScreenOpen, setFullScreenOpen] = useState(false);

  useEffect(() => {
    if (!document?.id || !open) {
      setFileUrl(null);
      return;
    }

    if (!isPreviewable(document.fileExtension)) {
      setFileUrl(null);
      return;
    }

    let cancelled = false;
    getDownloadLink({ variables: { id: document.id } }).then((result) => {
      if (!cancelled && result.data?.getDocumentDownloadLink) {
        setFileUrl(result.data.getDocumentDownloadLink);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [document?.id, document?.fileExtension, open, getDownloadLink]);

  const handleDownload = useCallback(async () => {
    if (!document) return;
    await fetchAndHandleDownload({ documentId: document.id });
  }, [document, fetchAndHandleDownload]);

  const handleEdit = useCallback(() => {
    if (!document || !onEdit) return;
    onEdit(document);
  }, [document, onEdit]);

  const facilities = useMemo(() => document?.facilities ?? [], [document?.facilities]);
  const chemicals = useMemo(() => document?.chemicalDocument ?? [], [document?.chemicalDocument]);
  const tags = document?.documentTags ?? [];
  const associatedReports = useMemo(
    () => document?.associatedReports ?? [],
    [document?.associatedReports]
  );

  const reportingYears = useMemo(() => {
    const years = [...new Set(associatedReports.map((r) => r.reportingYear))];
    years.sort((a, b) => b - a);
    return years;
  }, [associatedReports]);

  type RelatedLink = {
    label: string;
    href: string;
    icon: React.ReactNode;
  };

  const relatedLinks = useMemo(() => {
    if (!tenantId) return [];
    const links: RelatedLink[] = [];

    const epcraReports = associatedReports.filter((r) => r.programArea === "EPCRA" && r.reportId);
    const seenReportIds = new Set<string>();
    for (const r of epcraReports) {
      if (seenReportIds.has(r.reportId ?? "")) continue;
      seenReportIds.add(r.reportId ?? "");
      const kindLabel = r.reportKind
        ? reportKindToLabel(r.reportKind as TierIiReportKind)
        : "Tier II";
      links.push({
        label: `Report: ${r.reportingYear} ${kindLabel}`,
        href: `/o/${tenantId}/chemicals/reports/${r.reportId}`,
        icon: <AssignmentOutlined fontSize="small" color="action" />,
      });
    }

    const rcraSubmissions = associatedReports.filter(
      (r) => r.programArea === "RCRA" && r.submissionId
    );
    const seenSubmissionIds = new Set<string>();
    for (const r of rcraSubmissions) {
      if (seenSubmissionIds.has(r.submissionId ?? "")) continue;
      seenSubmissionIds.add(r.submissionId ?? "");
      links.push({
        label: `Submission: ${r.reportingYear} Waste`,
        href: `/o/${tenantId}/waste/submissions/${r.submissionId}`,
        icon: <SendOutlined fontSize="small" color="action" />,
      });
    }

    for (const f of facilities) {
      links.push({
        label: `Facility: ${f.name}`,
        href: `/o/${tenantId}/facilities/${f.id}`,
        icon: <BusinessOutlined fontSize="small" color="action" />,
      });
    }

    const hasRcra = associatedReports.some((r) => r.programArea === "RCRA");
    if (hasRcra) {
      for (const f of facilities) {
        links.push({
          label: `Waste: ${f.name}`,
          href: `/o/${tenantId}/waste/facilities/${f.id}`,
          icon: <DeleteOutline fontSize="small" color="action" />,
        });
      }
    }

    for (const cd of chemicals) {
      links.push({
        label: `Chemical: ${cd.chemical.name}`,
        href: `/o/${tenantId}/chemicals/catalog/chemicals/${cd.chemical.id}`,
        icon: <ScienceOutlined fontSize="small" color="action" />,
      });
    }

    links.push({
      label: `Catalog: ${tenantName ?? "All"} Documents`,
      href: `/o/${tenantId}/documents`,
      icon: <FolderOutlined fontSize="small" color="action" />,
    });

    return links;
  }, [tenantId, tenantName, associatedReports, facilities, chemicals]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "90%", sm: 576, md: 672 },
            boxSizing: "border-box",
          },
        }}
      >
        {document && (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
              }}
            >
              <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
                {document.title}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Download">
                  <IconButton size="small" onClick={handleDownload}>
                    <Download fontSize="small" />
                  </IconButton>
                </Tooltip>
                {onEdit && (
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={handleEdit}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {fileUrl && (
                  <Tooltip title="Full screen">
                    <IconButton size="small" onClick={() => setFullScreenOpen(true)}>
                      <Fullscreen fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton size="small" onClick={onClose}>
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
            </Box>

            <Divider />

            {isPreviewable(document.fileExtension) && (
              <Box
                sx={{
                  height: 360,
                  flexShrink: 0,
                  position: "relative",
                  overflow: "hidden",
                  p: 2,
                }}
              >
                {fileUrl ? (
                  <FilePreview
                    fileUrl={fileUrl}
                    fileExtension={document.fileExtension ?? undefined}
                  />
                ) : (
                  <Skeleton variant="rectangular" height="100%" animation="wave" />
                )}
              </Box>
            )}

            {!isPreviewable(document.fileExtension) && (
              <Box
                sx={{
                  height: 120,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "grey.50",
                  mx: 2,
                  my: 2,
                  borderRadius: 1,
                }}
              >
                <Stack alignItems="center" spacing={1}>
                  <InsertDriveFileOutlined sx={{ fontSize: 48, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary">
                    {document.fileExtension?.toUpperCase()} file — no preview available
                  </Typography>
                </Stack>
              </Box>
            )}

            <Divider />

            <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
              <Stack spacing={2.5}>
                <DetailRow label="Filename">
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {document.title}
                  </Typography>
                </DetailRow>

                <DetailRow label="Document Type">
                  <Typography variant="body2">
                    {documentTypeToLabel(document.documentType as DocumentType)}
                  </Typography>
                </DetailRow>

                <DetailRow label="Uploaded">
                  <Typography variant="body2">
                    {document.createdAt ? prettyPrintDateMed(document.createdAt) : "—"}
                  </Typography>
                </DetailRow>

                {document.authoredAt && (
                  <DetailRow label="Authored">
                    <Typography variant="body2">
                      {prettyPrintDateMed(document.authoredAt)}
                    </Typography>
                  </DetailRow>
                )}

                {document.description && (
                  <DetailRow label="Description">
                    <Typography variant="body2">{document.description}</Typography>
                  </DetailRow>
                )}

                {document.sizeMegabytes != null && (
                  <DetailRow label="Size">
                    <Typography variant="body2">
                      {document.sizeMegabytes < 1
                        ? `${Math.round(document.sizeMegabytes * 1024)} KB`
                        : `${document.sizeMegabytes.toFixed(1)} MB`}
                    </Typography>
                  </DetailRow>
                )}

                {facilities.length > 0 && (
                  <DetailRow label="Facilities">
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {facilities.map((f) => (
                        <Chip
                          key={f.id}
                          label={f.name}
                          size="small"
                          variant="outlined"
                          component={Link}
                          href={`/o/${tenantId}/facilities/${f.id}`}
                          clickable
                        />
                      ))}
                    </Stack>
                  </DetailRow>
                )}

                {reportingYears.length > 0 && (
                  <DetailRow label="Reporting Years">
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {reportingYears.map((year) => (
                        <Chip
                          key={year}
                          label={year}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </DetailRow>
                )}

                {chemicals.length > 0 && (
                  <DetailRow label="Chemical">
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {chemicals.map((cd) => (
                        <Chip
                          key={cd.chemical.id}
                          label={cd.chemical.name}
                          size="small"
                          variant="outlined"
                          component={Link}
                          href={`/o/${tenantId}/chemicals/catalog/chemicals/${cd.chemical.id}`}
                          clickable
                        />
                      ))}
                    </Stack>
                  </DetailRow>
                )}

                {tags.length > 0 && (
                  <DetailRow label="Tags">
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {tags.map((tag) => (
                        <Chip key={tag.id} label={tag.name} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </DetailRow>
                )}

                {document.currentSubmissionDocument && (
                  <DetailRow label="Status">
                    <Chip
                      label="Current Submission Document"
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </DetailRow>
                )}

                {relatedLinks.length > 0 && (
                  <>
                    <Divider />
                    <DetailRow label="Related Links">
                      <List dense disablePadding>
                        {relatedLinks.map((link) => (
                          <ListItem key={link.href} disableGutters disablePadding sx={{ py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>{link.icon}</ListItemIcon>
                            <ListItemText
                              primary={
                                <Link href={link.href} underline="hover" variant="body2">
                                  {link.label}
                                </Link>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </DetailRow>
                  </>
                )}
              </Stack>
            </Box>
          </Box>
        )}
      </Drawer>

      {fileUrl && (
        <FullScreenDocumentPreview
          open={fullScreenOpen}
          onClose={() => setFullScreenOpen(false)}
          fileUrl={fileUrl}
          title={document?.title}
        />
      )}
    </>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}
